import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { fetchIbamaOverview, ibamaCaseParams } from '@/services/ibama';
import type {
  QuantileSummary,
  AnswerOut,
  IbamaCurrencyFence,
  IbamaInstanceReach,
  IbamaNatureSplit,
  IbamaSubjectScope,
} from '@/services/types';

/** The case list filtered to one predicate this screen counts by. */
export function ibamaCasesHref(filters: Parameters<typeof ibamaCaseParams>[0]): string {
  const params = ibamaCaseParams(filters).toString();
  return params ? `/ibama/cases?${params}` : '/ibama/cases';
}

/**
 * IBAMA autos de infracao: every auto issued, and what happened to the ones
 * that entered collection.
 *
 * Two populations sit side by side on this page and are never summed: the
 * autos master (issuance) and SICAFI's debt ledger (the only rows with a
 * published outcome). Every rate is over the LEDGER and says so. There is no
 * fine-reduction waterfall -- the ledger dates the instances and records no
 * amount per instance -- and two fences the corpus forces are rendered as
 * partial-coverage cards computed per request: natural persons are counted
 * and never named, and pre-Real amounts enter no ladder.
 */
export function IbamaOverviewPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'overview'],
    queryFn: fetchIbamaOverview,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const stats: {
    key: string;
    value: number;
    href?: string;
    hrefLabel?: string;
    second?: string;
  }[] = [
    {
      key: 'autos',
      value: data.corpus.autos_issued,
      second: t('ibama:overview.corpus.cancelled', {
        n: number.format(data.corpus.autos_cancelled),
      }),
    },
    {
      key: 'ledger',
      value: data.corpus.ledger_cases,
      href: ibamaCasesHref({}),
      hrefLabel: t('ibama:overview.corpus.openLedger'),
      second: t('ibama:overview.corpus.ledgerShare', {
        share: percent.format(data.corpus.ledger_share_of_autos),
      }),
    },
    {
      key: 'firstDecided',
      value: data.corpus.first_decided,
      href: ibamaCasesHref({ instance: 'first' }),
      hrefLabel: t('ibama:overview.corpus.openFirstDecided'),
      second: t('ibama:overview.corpus.boardDated', {
        n: number.format(data.corpus.board_dated),
      }),
    },
    {
      key: 'entities',
      value: data.corpus.entities,
      href: '/ibama/entities',
      hrefLabel: t('ibama:overview.corpus.openEntities'),
      second: t('ibama:overview.corpus.companiesOnly'),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:overview.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:overview.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:overview.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path="/api/v1/ibama/export/overview"
          label={t('ibama:overview.exportOverview')}
        />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.key} className="rounded-lg border bg-card p-3" data-tile={stat.key}>
            <dt className="text-xs text-muted-foreground">
              {t(`ibama:overview.corpus.${stat.key}`)}
            </dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
              {stat.href ? (
                <Link
                  to={stat.href}
                  aria-label={stat.hrefLabel}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {number.format(stat.value)}
                </Link>
              ) : (
                number.format(stat.value)
              )}
            </dd>
            {stat.second ? (
              <dd className="mt-0.5 text-xs text-muted-foreground">{stat.second}</dd>
            ) : null}
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground" data-ledger-updated>
        {t('ibama:overview.corpus.updated', {
          ledger: data.corpus.ledger_updated?.slice(0, 10) ?? '—',
          master: data.corpus.master_updated?.slice(0, 10) ?? '—',
        })}
      </p>

      <FieldGuide i18nKey="ibama:overview.guide.corpus" level={2} />

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{
            ...data.collection_mix,
            value: data.collection_mix.value?.outcomes ?? null,
          }}
          titleKey="ibama:overview.collectionMix.title"
          helpKey="ibama:overview.collectionMix.help"
          labelPrefix="ibama:overview.outcomes."
          rowHref={(code) => ibamaCasesHref({ outcome: code })}
        />
        <FineDistributionPanel
          answer={data.penalty_distribution}
          titleKey="ibama:overview.faceValues.title"
          helpKey="ibama:overview.faceValues.help"
          countKey="ibama:overview.faceValues.count"
        />
      </div>

      <InstanceReachPanel answer={data.instance_reach} />

      <NatureSplitPanel answer={data.nature_split} />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:overview.fences.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('ibama:overview.fences.intro')}</p>
        <div className="grid gap-4 lg:grid-cols-2">
          <SubjectScopeCard answer={data.subject_scope} />
          <CurrencyFenceCard answer={data.currency_fence} />
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:overview.refusals.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('ibama:overview.refusals.intro')}</p>
        <div className="grid gap-4 lg:grid-cols-3">
          <RefusalCard
            title={t('ibama:overview.refusals.decisionMakers')}
            answer={data.decision_makers}
          />
          <RefusalCard
            title={t('ibama:overview.refusals.reasoning')}
            answer={data.reasoning_layer}
          />
          <RefusalCard
            title={t('ibama:overview.refusals.precedent')}
            answer={data.precedent_graph}
          />
        </div>
      </section>
    </div>
  );
}

/**
 * How far the ledger's cases got through the two instances: dates only.
 * PARTIAL while any case lacks both; the numbers are measured per request.
 */
function InstanceReachPanel({ answer }: { answer: AnswerOut<IbamaInstanceReach> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-instance-reach>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('ibama:overview.instances.title')}
      </h2>
      <Answer answer={answer} help={t('ibama:overview.instances.help')}>
        {(reach) => (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure
                label={t('ibama:overview.instances.firstDecided')}
                value={`${number.format(reach.first_decided)} (${percent.format(
                  reach.first_decided_share,
                )})`}
                href={ibamaCasesHref({ instance: 'first' })}
              />
              <Figure
                label={t('ibama:overview.instances.boardDated')}
                value={`${number.format(reach.board_dated)} (${percent.format(
                  reach.board_dated_share,
                )})`}
                href={ibamaCasesHref({ instance: 'board' })}
              />
              <Figure
                label={t('ibama:overview.instances.neither')}
                value={number.format(reach.neither_dated)}
                href={ibamaCasesHref({ instance: 'none' })}
              />
              <Figure
                label={t('ibama:overview.instances.outcomeRecorded')}
                value={number.format(reach.board_outcome_recorded)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('ibama:overview.instances.undatedOutcome', {
                n: number.format(reach.board_outcome_undated),
              })}
            </p>
          </div>
        )}
      </Answer>
      <div className="mt-3">
        <FieldGuide i18nKey="ibama:overview.guide.instances" />
      </div>
    </section>
  );
}

/**
 * The infraction nature: IBAMA's own classification, alphabetical, never a
 * comparison of natures as adjudicators.
 */
function NatureSplitPanel({ answer }: { answer: AnswerOut<IbamaNatureSplit> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-nature-split>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('ibama:overview.natures.title')}
      </h2>
      <Answer answer={answer} help={t('ibama:overview.natures.help')}>
        {(split) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ibama:overview.natures.nature')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:overview.natures.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:overview.natures.firstDecided')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:overview.natures.settled')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {split.rows.map((row) => (
                    <tr key={row.nature} className="border-t" data-nature={row.nature}>
                      <td className="px-3 py-2 text-foreground">{row.nature}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={ibamaCasesHref({ nature: row.nature })}
                          className="text-primary underline-offset-2 hover:underline"
                          aria-label={t('ibama:overview.natures.open', {
                            n: number.format(row.cases),
                            nature: row.nature,
                          })}
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.first_decided)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.settled)} ({percent.format(row.settled_share)})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {split.unattributed > 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('ibama:overview.natures.unattributed', {
                  n: number.format(split.unattributed),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}

/**
 * Natural persons are counted in every total and never named. PARTIAL by
 * construction: the badge is the disclosure, and the figures below it are
 * the measured split on both populations.
 */
function SubjectScopeCard({ answer }: { answer: AnswerOut<IbamaSubjectScope> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-subject-scope>
      <h3 className="mb-2 text-sm font-medium text-foreground">
        {t('ibama:overview.subjects.title')}
      </h3>
      <Answer answer={answer} help={t('ibama:overview.subjects.help')}>
        {(scope) => (
          <div className="grid grid-cols-2 gap-3">
            <Figure
              label={t('ibama:overview.subjects.ledgerCompanies')}
              value={`${number.format(scope.ledger_companies)} (${percent.format(
                scope.ledger_company_share,
              )})`}
              href={ibamaCasesHref({ subject: 'company' })}
            />
            <Figure
              label={t('ibama:overview.subjects.ledgerPersons')}
              value={number.format(scope.ledger_persons)}
              href={ibamaCasesHref({ subject: 'person' })}
            />
            <Figure
              label={t('ibama:overview.subjects.autosCompanies')}
              value={`${number.format(scope.autos_companies)} (${percent.format(
                scope.autos_company_share,
              )})`}
            />
            <Figure
              label={t('ibama:overview.subjects.autosPersons')}
              value={number.format(scope.autos_persons)}
            />
          </div>
        )}
      </Answer>
    </section>
  );
}

/** Pre-Real amounts enter no ladder; their counts stay everywhere else. */
function CurrencyFenceCard({ answer }: { answer: AnswerOut<IbamaCurrencyFence> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="rounded-lg border bg-card p-4" data-currency-fence>
      <h3 className="mb-2 text-sm font-medium text-foreground">
        {t('ibama:overview.currency.title')}
      </h3>
      <Answer answer={answer} help={t('ibama:overview.currency.help')}>
        {(fence) => (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <Figure
                label={t('ibama:overview.currency.brl')}
                value={number.format(fence.brl)}
                href={ibamaCasesHref({ currency: 'brl' })}
              />
              <Figure
                label={t('ibama:overview.currency.other')}
                value={number.format(fence.non_brl)}
                href={fence.non_brl ? ibamaCasesHref({ currency: 'other' }) : undefined}
              />
            </div>
            {fence.currencies.length ? (
              <p className="text-xs text-muted-foreground">
                {t('ibama:overview.currency.list', { list: fence.currencies.join(', ') })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}

/** A labelled figure, optionally a link into the ledger. Shared by every IBAMA page. */
export function Figure({ label, value, href }: { label: string; value: string; href?: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">
        {href ? (
          <Link to={href} className="text-primary underline-offset-2 hover:underline">
            {value}
          </Link>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

const QUANTILES = ['p10', 'p25', 'p50', 'p75', 'p90'] as const;

/** One quantile ladder as five cells; `format` renders the unit (reais, days, a share). */
export function QuantileCells({
  summary,
  format,
}: {
  summary: QuantileSummary | null;
  format: (value: number) => string;
}) {
  return (
    <dl className="grid grid-cols-5 gap-2 text-sm">
      {QUANTILES.map((q) => (
        <div key={q} className="rounded-md border bg-muted/30 p-2" data-quantile={q}>
          <dt className="text-xs text-muted-foreground">{q}</dt>
          <dd className="font-semibold tabular-nums text-foreground">
            {summary ? format(summary.quantiles[q] ?? 0) : '—'}
          </dd>
        </div>
      ))}
    </dl>
  );
}
